// src/services/organizerApi.js
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

// In-memory access token for organizer
let organizerAccessToken = null;

export const setOrganizerAccessToken = (token) => {
  organizerAccessToken = token;
};

export const getOrganizerAccessToken = () => organizerAccessToken;

export const clearOrganizerAccessToken = () => {
  organizerAccessToken = null;
};

// Create separate axios instance for organizer API
const organizerApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies (refresh token) with requests
});

// Track whether a refresh is in progress
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - use in-memory organizer token
organizerApi.interceptors.request.use(
  (config) => {
    if (organizerAccessToken) {
      config.headers.Authorization = `Bearer ${organizerAccessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with silent refresh
organizerApi.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if the failed request IS the refresh request
      if (originalRequest.url?.includes('/organizer/refresh')) {
        clearOrganizerAccessToken();
        if (!window.location.pathname.includes('/organizer/login')) {
          window.location.href = '/organizer/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return organizerApi(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${API_BASE_URL}/organizer/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = response.data?.data?.token;
        if (newToken) {
          setOrganizerAccessToken(newToken);
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return organizerApi(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearOrganizerAccessToken();
        if (!window.location.pathname.includes('/organizer/login')) {
          window.location.href = '/organizer/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For non-401 errors, show toast
    if (error.response?.status !== 401) {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        const message = error.response?.data?.message || 'An error occurred';
        toast.error(message);
      }
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const organizerAuthAPI = {
  login: (credentials) => organizerApi.post('/organizer/login', credentials),
  apply: (applicationData) => organizerApi.post('/organizer/apply', applicationData),
  getMe: () => organizerApi.get('/organizer/me'),
  updatePassword: (data) => organizerApi.put('/organizer/password', data),
  updateEmail: (data) => organizerApi.put('/organizer/email', data),
  uploadLogo: (formData) => organizerApi.post('/organizer/logo', formData, {
    headers: { 'Content-Type': undefined }
  }),
  removeLogo: () => organizerApi.delete('/organizer/logo'),
  refresh: () => axios.post(`${API_BASE_URL}/organizer/refresh`, {}, { withCredentials: true }),
  logout: () => organizerApi.post('/organizer/logout'),
};

// Events endpoints
export const organizerEventsAPI = {
  getMyEvents: (params = {}) => organizerApi.get('/events/my-events', { params }),
  createEvent: (eventData) => organizerApi.post('/events/create', eventData, {
    headers: { 'Content-Type': undefined }
  }),
  getEvent: (id) => organizerApi.get(`/events/${id}`),
  updateEvent: (id, eventData) => organizerApi.put(`/events/${id}`, eventData, {
    headers: { 'Content-Type': undefined }
  }),
  deleteEvent: (id) => organizerApi.delete(`/events/${id}`),
  submitEvent: (id) => organizerApi.post(`/events/${id}/submit`),
  getEventRegistrations: (id) => organizerApi.get(`/events/${id}/registrations`),
};

export default organizerApi;
